import React, { useState, useContext } from "react";
import { useEffect, useRef } from "react";
import { DivisionLensContext, DivisionConfigContext, DataActionsContext } from "@/App";
import { divisionColor, divisionLabel, itemDivision, divArea, divFlag, divStatusOptions, divStatusLabel, divTerminalKey } from "@/lib/divisions";
import DivisionGlyph from "@/components/icons/DivisionGlyph";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, ShieldAlert, ShieldOff, PackageX,
  Pencil, Eye, Zap, ClipboardCheck, CheckCircle2, Clock3,
  Truck, Wrench, Timer, RotateCcw, Link2, SquareCheck, ExternalLink,
  CheckCheck, MapPin, Calendar, Plus,
  Package, ShoppingCart, Factory, Layers, ChevronRight, Lock, Hourglass, X,
  Route, Warehouse,
} from "lucide-react";
import ReworkIcon from "../icons/ReworkIcon";
import { useBlockCatalogue, openBlocks, blockAgeDays, promiseState, blockHeadline } from "@/lib/blockCatalogue";
import StatusGlyph from "../icons/StatusGlyph";
import RouterCardIcon from "../icons/RouterCardIcon";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import KittingTrayIcon from "../icons/KittingTrayIcon";
import CncMachineIcon from "../icons/CncMachineIcon";
import NotesCell from "../NotesCell";
import { FreshDot } from "../LiveIndicator";
import { BuildTypeToggle, ChildPartsPill, PartialProcuredTag } from "../bom/BomBits";
import { isAssembly } from "../bom/bomUtils";

/**
 * useAdvanceLustre — full sweep for 10s, then drop to subtle breathing until
 * the user hovers the button (stops everything) or the item leaves the
 * "Machining Complete" state. Only applies on the Manufacturing stage.
 * Returns: "full" | "breathe" | "off".
 */
function useAdvanceLustre(item, stage, advanceable) {
  const active = stage === "Manufacturing" && advanceable && item.manufacturing_status === "Machining Complete";
  const [phase, setPhase] = useState(active ? "full" : "off");
  const startRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setPhase("off");
      startRef.current = null;
      return;
    }
    if (startRef.current == null) {
      startRef.current = Date.now();
      setPhase("full");
    }
    const elapsed = Date.now() - startRef.current;
    if (elapsed >= 10000) {
      setPhase("breathe");
      return;
    }
    const t = setTimeout(() => setPhase("breathe"), 10000 - elapsed);
    return () => clearTimeout(t);
  }, [active, item.manufacturing_status]);

  return phase;
}
import {
  PROCUREMENT_STATUSES, MANUFACTURING_STATUSES, QC_STATUSES, DISPATCH_STATUSES,
  PROC_TEAM_COLORS, DispatchDueCell, CompletedDueCell, CompletedOnCell, formatCurrency,
  getStageAgeSecs, formatStageAge, ageColor, ProcStatusBadge, formatEtaShort, formatDateDMY,
  TrInvoiceCell, carrierTrackingUrl, openTrackingUrl,
} from "./utils";

// Map a procurement status field to its companion eta field
const ETA_FIELD = {
  material_status: "material_eta",
  tooling_status:  "tooling_eta",
  gauging_status:  "gauging_eta",
};

const STAGE_META = {
  "PO Received":     { short: "PO R",  path: "/po-received",   color: "#3b82f6", Icon: Package },
  "Procurement":     { short: "PROC",  path: "/procurement",   color: "#f97316", Icon: ShoppingCart },
  "Manufacturing":   { short: "MFG",   path: "/manufacturing", color: "#a855f7", Icon: Factory },
  "Quality Control": { short: "QC",    path: "/qc-check",      color: "#14b8a6", Icon: ClipboardCheck },
  "Dispatch":        { short: "DISP",  path: "/dispatch",      color: "#f59e0b", Icon: Truck },
  "Completed":       { short: "DONE",  path: "/completed",     color: "#94a3b8", Icon: CheckCircle2 },
};
// Pipeline order — used to sort the "also in" stage pills left→right like the flow.
const STAGE_ORDER = Object.keys(STAGE_META);

// Status-driven palette for the procurement subteam dropdowns. Sub-team color
// stays on the row label (Material/Tooling/Gauging); the chip itself reflects
// the *status* so a quick glance answers "what's done / what's stuck?".
const PROC_STATUS_COLORS = {
  Pending:               { color: "#64748b", bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.30)" }, // slate
  Ordered:               { color: "#3b82f6", bg: "rgba(59,130,246,0.10)",  border: "rgba(59,130,246,0.30)"  }, // blue
  "Partially Received":  { color: "#f59e0b", bg: "rgba(245,158,11,0.10)",  border: "rgba(245,158,11,0.30)"  }, // amber
  Complete:              { color: "#10b981", bg: "rgba(16,185,129,0.10)",  border: "rgba(16,185,129,0.35)"  }, // emerald
};

// Status dot palette removed — replaced by `<StatusGlyph status=… />` which
// renders an expressive small SVG (filled tick / hollow / dashed / flag /
// double-tick) per status instead of a flat colored dot.

/* ── Status-tinted select pills (depth pass) ─────────────────────────────
 * State is expressed by tint, not by a gray box: not-started = dashed
 * neutral (same "pending" vocabulary as the route-card / kitting / ERP
 * chips), in-flight = blue tint, done = emerald, failed/rework = red.
 * Depth (hairline shadow + top highlight) comes from .rl-pill in App.css,
 * which Performance Mode flattens. Shared with StageRowMobile. */
const STATUS_TONES = {
  pending: "border border-dashed border-[hsl(var(--border))] bg-transparent text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/40 data-[state=open]:bg-[hsl(var(--muted))]/40",
  blue:    "rl-pill border border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-400 hover:bg-blue-500/10 data-[state=open]:bg-blue-500/10",
  amber:   "rl-pill border border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 data-[state=open]:bg-amber-500/10",
  orange:  "rl-pill border border-orange-500/40 bg-orange-500/5 text-orange-700 dark:text-orange-400 hover:bg-orange-500/10 data-[state=open]:bg-orange-500/10",
  purple:  "rl-pill border border-purple-500/35 bg-purple-500/5 text-purple-700 dark:text-purple-400 hover:bg-purple-500/10 data-[state=open]:bg-purple-500/10",
  teal:    "rl-pill border border-teal-500/40 bg-teal-500/5 text-teal-700 dark:text-teal-400 hover:bg-teal-500/10 data-[state=open]:bg-teal-500/10",
  done:    "rl-pill border border-emerald-500/35 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 data-[state=open]:bg-emerald-500/10",
  red:     "rl-pill border border-red-500/35 bg-red-500/5 text-red-700 dark:text-red-400 hover:bg-red-500/10 data-[state=open]:bg-red-500/10",
};
// Tint/outline/text follow the SAME hue the status glyph uses (see
// icons/StatusGlyph.js + CncMachineIcon's third-party state) so the pill
// never argues with its own icon.
const STATUS_TONE_BY_VALUE = {
  "Complete": "done", "Machining Complete": "done", "Passed": "done", "Shipped": "done",
  "Ordered": "blue", "Processing": "blue", "Under Inspection": "blue", "Delivered": "blue",
  "Partially Received": "amber", "Pickup Awaited": "amber", "Negotiating Rates": "amber", "Shipment Call Raised": "amber",
  "Third Party": "orange",
  "In Setup": "purple",
  "Rework Required": "teal",
  "Failed": "red",
};
export function statusTone(v) {
  const s = (v || "").trim();
  if (!s || s === "Pending" || s === "Pending Inspection" || s === "Queued") return "pending";
  return STATUS_TONE_BY_VALUE[s] || "blue";
}
export const statusTriggerCls = (v) =>
  `shrink-0 gap-1.5 rounded-full transition-colors shadow-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-[hsl(var(--ring))]/60 font-medium [&>svg]:opacity-70 [&>svg]:transition-transform data-[state=open]:[&>svg]:rotate-180 ${STATUS_TONES[statusTone(v)]}`;

/* ── Status control sizing ───────────────────────────────────────────────
 * ONE source of truth for the shape of every status control in the row
 * (the stage dropdowns AND the pills stacked beneath them). Only the WIDTH
 * varies per stage — set by content ("Machining Complete" needs more room
 * than "Shipped") — everything else (height, padding, text, icon slot) is
 * identical across Procurement / Manufacturing / QC / Dispatch, in both
 * densities. STATUS_ICON_PX keeps the leading glyph one size everywhere.
 * Fixes the pre-refactor drift: 5 widths, 3 paddings, 3 icon sizes.  */
export const STATUS_ICON_PX = 14;
export const STATUS_CTL_W = {
  // Procurement is deliberately the NARROWEST: its 3 everyday statuses are
  // short (Pending / Ordered / Complete) and the row also carries an ETA badge
  // to the dropdown's right, so wasted width here squeezes the ETA. 120px hugs
  // the short statuses; the lone long value ("Partially Received") truncates to
  // "Partially…" (full text in the tooltip) — an accepted trade for a tight,
  // stable column. The other stages stay content-fit to their longest label.
  Procurement: "w-[120px]",
  Manufacturing: "w-[180px]",
  "Quality Control": "w-[170px]",
  Dispatch: "w-[160px]",
};
export const statusCtlSize = (widthCls, isCompact) =>
  // LEFT-PACK the icon + status label. The base trigger is justify-between,
  // which floats the value to the middle when there are 3 children (icon,
  // value, chevron) — that's the inconsistency vs. the pills below. We flip it
  // to justify-start and shove ONLY the trailing chevron to the right edge with
  // [&>svg:last-child]:ml-auto. Robust across all four stages regardless of
  // whether the leading icon is an <svg> (Proc/QC/Dispatch) or a <span> (Mfg).
  `${isCompact ? "h-6" : "h-7"} ${widthCls} px-2.5 text-xs [justify-content:flex-start] [&>svg:last-child]:ml-auto`;

/**
 * QualityDossierToggle — appears under the QC dropdown only when qc_status="Passed".
 * Pulsing dashed circle before sign-off → filled emerald tick after.
 * Calls onToggle(itemId, !ready) which the parent persists via editItem.
 */
function QualityDossierToggle({ itemId, ready, onToggle, isCompact }) {
  return (
    <button
      type="button"
      onClick={() => onToggle && onToggle(itemId, !ready)}
      className={`group inline-flex items-center gap-1.5 ${isCompact ? "h-6" : "h-7"} px-2.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider transition-all w-[170px] whitespace-nowrap ${
        ready
          ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
          : "border-dashed border-amber-500/60 bg-amber-500/5 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15 qd-pulse"
      }`}
      aria-pressed={ready}
      data-testid={`qd-toggle-${itemId}`}
      title={ready ? "Quality Dossier Ready — click to un-mark" : "Click to mark Quality Dossier Ready"}
    >
      {ready ? (
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
      ) : (
        <span
          className="w-3.5 h-3.5 rounded-full border border-dashed border-amber-500/80 shrink-0 inline-block qd-dash-spin"
          aria-hidden="true"
        />
      )}
      <span>{ready ? "QD Ready" : "QD Pending"}</span>
    </button>
  );
}

/**
 * CustomerAcceptanceToggle — third QC gate that must be confirmed AFTER
 * quality_dossier_ready before the Advance button unlocks.
 * Powder-blue dashed square (pending) → green checked square (accepted).
 */
function CustomerAcceptanceToggle({ itemId, accepted, onToggle, disabled, isCompact }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle && onToggle(itemId, !accepted)}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 ${isCompact ? "h-6" : "h-7"} px-2.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider transition-all w-[170px] whitespace-nowrap ${
        disabled
          ? "border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]/40 cursor-not-allowed bg-transparent"
          : accepted
          ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
          : "border-dashed border-sky-400/70 bg-sky-500/5 text-sky-600 dark:text-sky-400 hover:bg-sky-500/15 cursor-pointer"
      }`}
      aria-pressed={accepted}
      data-testid={`customer-acceptance-toggle-${itemId}`}
      title={disabled ? "Set QD Ready first" : accepted ? "Customer Accepted — click to un-mark" : "Click to mark Customer Acceptance"}
    >
      {accepted ? (
        <SquareCheck className="w-3.5 h-3.5 shrink-0" strokeWidth={2.2} />
      ) : (
        <span
          className={`w-3.5 h-3.5 rounded-sm border-2 border-dashed shrink-0 inline-block ${disabled ? "border-[hsl(var(--border))]" : "border-sky-400/70"}`}
          aria-hidden="true"
        />
      )}
      <span>{accepted ? "Cust. Accepted" : "Cust. Accept Pending"}</span>
    </button>
  );
}

/** Stages where the per-part ERP (accounting) code is shown under PO/LN. */
export const ERP_VISIBLE_STAGES = new Set(["Manufacturing", "Quality Control", "Dispatch", "Completed"]);

/* ── Multi route-card serialisation ──────────────────────────────────────
 * Several physical route cards can authorise one line (e.g. one per
 * sub-assembly). They live in the SINGLE router_card_number string as
 * "NUM — LABEL, NUM — LABEL" so the backend, exports, kitting gate and
 * changelog are untouched. parseRouteCards splits on commas (which also
 * un-crams legacy free-text like "26390- E-Series, 26272- Bottom Sub"),
 * and the em-dash separates our structured number/label pairs. */
export function parseRouteCards(raw) {
  const s = (raw || "").trim();
  if (!s) return [];
  return s.split(",").map(seg => {
    const t = seg.trim();
    if (!t) return null;
    const i = t.indexOf("—");
    if (i > -1) return { num: t.slice(0, i).trim(), label: t.slice(i + 1).trim() };
    return { num: t, label: "" };
  }).filter(Boolean);
}
export function serializeRouteCards(cards) {
  return cards
    .map(c => ({ num: (c.num || "").trim(), label: (c.label || "").trim() }))
    .filter(c => c.num)
    .map(c => (c.label ? `${c.num} — ${c.label}` : c.num))
    .join(", ");
}
export const routeCardsTitle = (cards) =>
  cards.map(c => (c.label ? `${c.num} (${c.label})` : c.num)).join("\n");

/* ── ERP code serialisation ──────────────────────────────────────────────
 * A part can carry several ERP (accounting) codes. They live in the SINGLE
 * erp_code string, space-separated, so the backend, backfill, exports and
 * changelog stay untouched. parseErpCodes splits on any whitespace/comma/
 * newline/tab, trims, and de-dupes case-insensitively (first spelling wins);
 * serializeErpCodes joins with a single space. Mirrors parseRouteCards. */
export function parseErpCodes(raw) {
  const seen = new Set();
  const out = [];
  for (const tok of (raw || "").split(/[\s,]+/)) {
    const t = tok.trim();
    if (!t) continue;
    const key = t.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}
export const serializeErpCodes = (codes) => codes.map(c => c.trim()).filter(Boolean).join(" ");

/**
 * ErpBadge — the tiny "ERP" name-tag that leads every ERP chip. Replaces the
 * old $ receipt glyph, which read ambiguously (looked like a cost/value). A
 * literal sans label needs no learning. `tone`: "on" (emerald, code present)
 * or "muted" (grey, empty/add). Sizes with the chip via isCompact.
 */
function ErpBadge({ tone = "on", isCompact }) {
  const size = isCompact ? "text-[7px] px-[2px] py-[0.5px]" : "text-[8px] px-[3px] py-[1px]";
  const toneCls = tone === "on"
    ? "text-emerald-700 dark:text-emerald-400 bg-emerald-500/15"
    : "text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))]";
  return (
    <span className={`font-sans font-semibold tracking-wide leading-none rounded-[3px] shrink-0 ${size} ${toneCls}`} aria-hidden="true">ERP</span>
  );
}

/**
 * ErpCodeChip — the per-PART accounting-system code, shown under the PO/LN
 * block from Manufacturing onwards. The code is a one-time property of the
 * part number (the backend backfills every sibling line), so:
 *   · no code  → dashed "+ ERP code" affordance for authorized users
 *                (`canErpEdit`: Manufacturing editors on their stage; Admin +
 *                Manufacturing editors downstream — QC/Dispatch roles see an
 *                inert "ERP pending"), the exception path for legacy/missed
 *                codes
 *   · code set → emerald chip; click-to-change for Admin only, read-only
 *                for everyone else
 * `highlight` pulses the chip (erp-attention) when the advance flow flags a
 * missing code. Shared by StageRowDesktop and StageRowMobile.
 */
export function ErpCodeChip({ item, stage, canErpEdit, isAdmin, onErpClick, highlight, isCompact }) {
  const code = item.erp_code;
  const isMfg = stage === "Manufacturing";
  const sizeCls = isCompact ? "text-[9px] px-1 py-[0.5px] gap-0.5" : "text-[10px] px-1.5 py-[1.5px] gap-1";
  const base = `inline-flex items-center mt-1 rounded border leading-none whitespace-nowrap max-w-full ${sizeCls}`;

  if (!code) {
    if (canErpEdit) {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onErpClick?.(item); }}
          className={`${base} border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-emerald-500/60 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors ${highlight ? "erp-attention" : ""}`}
          title={isMfg
            ? "Enter the ERP (accounting) code for this part number — applies to every line with this part"
            : "Enter the ERP code (normally done during Manufacturing) — applies to every line with this part"}
          data-testid={`erp-chip-${item.id}`}
        >
          <ErpBadge tone="muted" isCompact={isCompact} />
          <span className="font-semibold">+ add</span>
        </button>
      );
    }
    return (
      <span
        className={`${base} border-dashed border-[hsl(var(--border))]/70 text-[hsl(var(--muted-foreground))]/60 ${highlight ? "erp-attention" : ""}`}
        title="No ERP code on file for this part number yet"
        data-testid={`erp-chip-${item.id}`}
      >
        <ErpBadge tone="muted" isCompact={isCompact} />
        pending
      </span>
    );
  }

  const codes = parseErpCodes(code);
  return (
    <ErpCodeChipSet
      item={item}
      codes={codes}
      base={base}
      isAdmin={isAdmin}
      onErpClick={onErpClick}
      isCompact={isCompact}
    />
  );
}

/**
 * ErpCodeChipSet — the emerald chip once a part has one or more ERP codes.
 *   · single code  → plain chip (Admin: click to change; others: read-only)
 *   · many codes   → compact "FIRST +N" chip; hover reveals a popover listing
 *                    every code with a copy-all action; click opens the editor
 *                    for Admins (read-only for everyone else).
 * The popover is HOVER-STABLE: a shared enter/leave group wraps both the chip
 * and the panel with a short close delay, so moving the mouse from the chip
 * into the panel (to hit copy-all) never dismisses it. Respects the app's
 * greyed-with-reason bar via titles; keyboard-focusable via the button.
 */
export function ErpCodeChipSet({ item, codes, base, isAdmin, onErpClick, isCompact }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const setCls = "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-mono-data font-semibold";

  const openNow = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } setOpen(true); };
  const closeSoon = () => { if (closeTimer.current) clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 160); };
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const first = codes[0] || "";
  const extra = codes.length - 1;
  const clickable = isAdmin;
  const handleClick = (e) => { e.stopPropagation(); if (clickable) onErpClick?.(item); };
  const changeHint = clickable ? "click to change (Admin)" : "only an Admin can change it";

  // Single code: keep the original simple chip (no popover needed).
  if (codes.length <= 1) {
    const title = `ERP code · ${first} — ${changeHint}`;
    if (clickable) {
      return (
        <button type="button" onClick={handleClick}
          className={`${base} ${setCls} hover:bg-emerald-500/20 transition-colors`}
          title={title} data-testid={`erp-chip-${item.id}`}>
          <ErpBadge tone="on" isCompact={isCompact} />
          <span className="truncate">{first}</span>
        </button>
      );
    }
    return (
      <span className={`${base} ${setCls}`} title={title} data-testid={`erp-chip-${item.id}`}>
        <ErpBadge tone="on" isCompact={isCompact} />
        <span className="truncate">{first}</span>
      </span>
    );
  }

  const copyAll = (e) => {
    e.stopPropagation();
    const text = serializeErpCodes(codes);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${codes.length} ERP codes`)).catch(() => toast.error("Couldn’t copy"));
    }
  };

  return (
    <span className="relative inline-flex align-top" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button type="button" onClick={handleClick}
        className={`${base} ${setCls} ${clickable ? "hover:bg-emerald-500/20" : ""} transition-colors`}
        title={`${codes.length} ERP codes · ${changeHint} — hover to see all`}
        data-testid={`erp-chip-${item.id}`}>
        <ErpBadge tone="on" isCompact={isCompact} />
        <span className="truncate">{first}</span>
        <span className="ml-0.5 rounded bg-emerald-600/20 dark:bg-emerald-400/20 px-1 leading-none font-bold shrink-0">+{extra}</span>
      </button>

      {open && (
        <div
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          className="absolute left-0 top-full z-30 mt-1 w-[260px] max-w-[80vw] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--popover))] text-[hsl(var(--popover-foreground))] shadow-lg p-2.5 cursor-default"
          data-testid={`erp-popover-${item.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">{codes.length} ERP codes</span>
            <button type="button" onClick={copyAll}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
              data-testid={`erp-copy-all-${item.id}`}>
              <Layers className="w-3 h-3" /> copy all
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {codes.map((c, i) => (
              <span key={i} className="font-mono-data text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 border border-emerald-500/35 bg-emerald-500/10 rounded px-1.5 py-0.5 leading-none">{c}</span>
            ))}
          </div>
          <div className="mt-2 pt-1.5 border-t border-[hsl(var(--border))] text-[9px] text-[hsl(var(--muted-foreground))]">
            {clickable ? "Click the chip to add or remove codes" : "Only an Admin can change these"}
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * AlsoInMarker — the cross-stage planning nudge. Flags that this same part is
 * also in progress on other line items/stages, so a team can batch the work
 * instead of setting up the same part twice (e.g. MFG sees "also in PROC" and
 * checks whether to plan the runs together). Sits INLINE right of the Rev
 * chip; the header carries a matching "also in" legend key. Three tiers:
 *   · rest      — a whisper: route icon + count of other lines ("⤳ 2").
 *   · row hover — a FLOATING bloom overlay (rl-also-bloom: absolute over the
 *                 chip, z-raised) fades in with "also in" + up to two named
 *                 stage pills (+N past that). It grows rightward OVER the Qty
 *                 column on its own layer, so the table NEVER reflows — the
 *                 wrapping Part # line cannot shake. Clicking the bloom opens
 *                 the popover too. Keyboard: group-focus-within/row shows it.
 *   · click     — detail popover: every other line with PO/LN/Qty + jump-to.
 * Colour lives ONLY on the stage pills (StagePill grammar); the chip is neutral.
 */
function AlsoInMarker({ item, duplicateOthers, dupStages, partNumStr, navigate }) {
  const [open, setOpen] = useState(false);

  const stagePill = (st, key) => {
    const m = STAGE_META[st] || { short: (st || "?").slice(0, 4), color: "#888", Icon: Layers };
    const SIcon = m.Icon;
    return (
      <span key={key} className="inline-flex items-center gap-[3px] text-[10px] font-semibold px-1 py-0.5 rounded leading-none shrink-0"
        style={{ background: m.color + "1a", color: m.color, border: `1px solid ${m.color}40` }}>
        <SIcon className="w-2.5 h-2.5" />{m.short}
      </span>
    );
  };
  const shownStages = dupStages.slice(0, 2);
  const extraStages = dupStages.length - shownStages.length;
  const summary = `Same part is also on ${duplicateOthers.length} other line item${duplicateOthers.length > 1 ? "s" : ""} — click to see them and plan the work together`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <span className="relative inline-flex self-stretch shrink-0" onPointerEnter={e => e.stopPropagation()}>
        <PopoverTrigger asChild>
          <button type="button" className="rl-also-chip" title={summary}
            onClick={e => e.stopPropagation()}
            data-testid={`dup-marker-${item.id}`}>
            <Route className="w-3 h-3 shrink-0 text-[hsl(var(--muted-foreground))]" />
            <span className="font-mono-data text-[11px] font-semibold leading-none text-[hsl(var(--foreground))]/75">{duplicateOthers.length}</span>
          </button>
        </PopoverTrigger>
        {/* Row-hover bloom — floating overlay pinned over the chip. Repeats the
            icon + count so it reads as the chip grown in place, then names the
            stages. Clickable (opens the same popover); hidden from readers
            (the chip's title carries the meaning). */}
        <span
          className="rl-also-bloom opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto group-focus-within/row:opacity-100 group-focus-within/row:pointer-events-auto"
          aria-hidden="true"
          onClick={e => { e.stopPropagation(); setOpen(true); }}
        >
          <Route className="w-3 h-3 shrink-0 text-[hsl(var(--muted-foreground))]" />
          <span className="font-mono-data text-[11px] font-semibold leading-none text-[hsl(var(--foreground))]/75">{duplicateOthers.length}</span>
          <span className="text-[10.5px] font-medium leading-none whitespace-nowrap text-[hsl(var(--muted-foreground))]">also in</span>
          {shownStages.map((st, i) => stagePill(st, i))}
          {extraStages > 0 && (
            <span className="text-[10px] font-semibold leading-none whitespace-nowrap text-[hsl(var(--muted-foreground))]/80">+{extraStages}</span>
          )}
        </span>
      </span>
      <PopoverContent side="bottom" align="start" className="w-auto min-w-[240px] max-w-[280px] p-0 text-sm rounded-lg shadow-lg border-[hsl(var(--border))]">
        {/* Header — teaching title + the shared part number as a small mono chip */}
        <div className="flex items-start gap-2 px-3 pt-2.5 pb-2">
          <Route className="w-3.5 h-3.5 shrink-0 mt-[1px] text-[hsl(var(--muted-foreground))]" />
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] font-semibold text-[hsl(var(--foreground))]/90 leading-tight">Same part, also in progress</div>
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] leading-snug mt-0.5">Plan the work together to save a setup.</div>
          </div>
          <span className="font-mono-data text-[9.5px] font-medium px-1.5 py-0.5 rounded rl-carved bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] whitespace-nowrap max-w-[92px] overflow-hidden text-ellipsis shrink-0" title={partNumStr}>
            {partNumStr}
          </span>
        </div>
        <div className="grid grid-cols-[48px_1fr_24px_28px_16px] items-center gap-0 px-3 py-1.5 border-t border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
          <span className="text-[9px] font-semibold tracking-wide text-[hsl(var(--muted-foreground))]">Stage</span>
          <span className="text-[9px] font-semibold tracking-wide text-[hsl(var(--muted-foreground))] pl-2">PO</span>
          <span className="text-[9px] font-semibold tracking-wide text-[hsl(var(--muted-foreground))] text-right">LN</span>
          <span className="text-[9px] font-normal tracking-wide text-[hsl(var(--muted-foreground))]/50 text-right">Qty</span>
          <span />
        </div>
        {duplicateOthers.map((occ, idx) => {
          const meta = STAGE_META[occ.stage] || { short: (occ.stage || "?").slice(0, 4), path: "/", color: "#888", Icon: Layers };
          const StIcon = meta.Icon;
          return (
            <button
              key={occ.id || idx}
              type="button"
              onClick={() => navigate(`${meta.path}?q=${encodeURIComponent(partNumStr)}`)}
              className={`group/dupnav grid grid-cols-[48px_1fr_24px_28px_16px] items-center gap-0 w-full px-3 py-1.5 text-left hover:bg-[hsl(var(--muted))]/50 transition-colors ${idx > 0 ? "border-t border-[hsl(var(--border))]/50" : ""}`}
              title={`Jump to ${meta.short} · PO ${occ.po_number}`}
              data-testid={`dup-nav-${occ.id}`}
            >
              <span
                className="inline-flex items-center gap-[3px] text-[9px] font-bold tracking-wide px-1 py-0.5 rounded"
                style={{ background: meta.color + "0d", color: meta.color, border: `1px solid ${meta.color}40` }}
              >
                <StIcon className="w-2.5 h-2.5" />
                {meta.short}
              </span>
              <span
                className="font-mono-data text-[11px] font-medium text-[hsl(var(--foreground))]/70 pl-2 overflow-hidden text-ellipsis whitespace-nowrap"
                title={occ.po_number}
              >
                {occ.po_number}
              </span>
              <span className="font-mono-data text-[11px] font-medium text-[hsl(var(--foreground))]/70 text-right whitespace-nowrap pr-1">
                {occ.line_number}
              </span>
              <span className="font-mono-data text-[10px] font-normal text-[hsl(var(--muted-foreground))]/60 text-right whitespace-nowrap">
                {occ.quantity}
              </span>
              <ChevronRight className="w-3 h-3 text-[hsl(var(--muted-foreground))] transition-transform duration-150 group-hover/dupnav:translate-x-[2px]" />
            </button>
          );
        })}
        <div className="px-3 py-1.5 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 text-[9.5px] text-[hsl(var(--muted-foreground))]">
          Click a line to jump to it
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * MfgGateToggles — two-step gate shown on the Manufacturing stage before the
 * status dropdown is unlocked. Step 1 asks for a Router Card #, Step 2 is a
 * single-click kitting confirmation. Both use the same pulsing-dashed →
 * emerald-tick vocabulary as the QC Quality Dossier toggle so operators
 * learn the pattern once.
 *
 * Props:
 *   item             — full line-item doc (needs router_card_number,
 *                      kitting_complete, router_released_by, kitting_complete_by)
 *   onSetRouter(id, rc)       — persists RC# (null clears); shows dialog if rc empty
 *   onSetKitting(id, bool)    — flips kitting flag
 */
export function MfgGateToggles({ item, onSetRouter, onSetKitting, isCompact, showKitting = true }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // Multi route-card draft: [{ num, label }]. Serialised into the single
  // router_card_number string (see parse/serializeRouteCards) — no backend
  // schema change, and legacy free-text values open pre-split by comma.
  const [draftCards, setDraftCards] = useState([{ num: "", label: "" }]);
  const routerReleased = !!item.router_card_number;
  const kitOk = !!item.kitting_complete;
  const releasedCards = parseRouteCards(item.router_card_number);

  const openRouterDialog = () => {
    const parsed = parseRouteCards(item.router_card_number);
    setDraftCards(parsed.length ? parsed : [{ num: "", label: "" }]);
    setDialogOpen(true);
  };
  const setCard = (idx, patch) =>
    setDraftCards(cards => cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const addCard = () => setDraftCards(cards => [...cards, { num: "", label: "" }]);
  const removeCard = (idx) =>
    setDraftCards(cards => (cards.length > 1 ? cards.filter((_, i) => i !== idx) : cards));

  const saveRouter = async () => {
    const filled = draftCards.filter(c => (c.num || "").trim());
    if (filled.length === 0) {
      toast.error("Enter at least one Route Card #");
      return;
    }
    if (filled.some(c => c.num.trim().length < 3)) {
      toast.error("Each Route Card # needs at least 3 characters");
      return;
    }
    try {
      await onSetRouter(item.id, serializeRouteCards(filled));
      setDialogOpen(false);
    } catch { /* toast already shown by mutation */ }
  };
  const clearRouter = async () => {
    try {
      await onSetRouter(item.id, null);
      setDialogOpen(false);
    } catch { /* noop */ }
  };

  // Unified 200px width on both buttons so they visually align with the
  // status dropdown directly below. Left-icon slot is a fixed 18px box so
  // every row's label starts at the same x-offset.
  const btnBase = `group inline-flex items-center gap-2 ${isCompact ? "h-6" : "h-7"} px-2.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider transition-all w-full md:w-[180px]`;
  // Fixed SQUARE-ish slot, icon centred: every leading icon (RC ~18px wide,
  // Kitting ~18px, the dropdown's Queued circle 12px) shares ONE horizontal
  // centre-line at the slot midpoint regardless of its own width. The Mfg
  // status dropdown below uses the identical w-5 slot so its icon lines up.
  const iconSlot = "inline-flex items-center justify-center w-5 h-[14px] shrink-0";

  return (
    <div className={isCompact ? "flex flex-col gap-0.5" : "flex flex-col gap-1"}>
      {/* Step 1 — Route Card */}
      <button
        type="button"
        onClick={openRouterDialog}
        className={`${btnBase} ${
          routerReleased
            ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
            : "border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60 hover:text-[hsl(var(--foreground))]"
        }`}
        aria-pressed={routerReleased}
        data-testid={`mfg-gate-router-${item.id}`}
        title={routerReleased
          ? `Route Card${releasedCards.length > 1 ? "s" : ""} released:\n${routeCardsTitle(releasedCards)}${item.router_released_by ? "\nby " + item.router_released_by : ""}\n— click to edit`
          : "Release the route card"
        }
      >
        <span className={iconSlot}>
          <RouterCardIcon size={14} state={routerReleased ? "complete" : "pending"} />
        </span>
        <span className="truncate">
          {routerReleased ? <><span className="text-[hsl(var(--muted-foreground))] font-normal">RC: </span>{releasedCards[0]?.num || item.router_card_number}</> : "Route card pending"}
        </span>
        {releasedCards.length > 1 && (
          <span className="shrink-0 font-mono-data text-[9px] font-bold px-1.5 py-px rounded-full bg-emerald-500/15 leading-none">+{releasedCards.length - 1}</span>
        )}
      </button>

      {/* Step 2 — Kitting (locked until Step 1 complete). Hidden for divisions
          whose kitting gate is off (drilling — Phase 1b). */}
      {showKitting && (
      <button
        type="button"
        onClick={() => routerReleased && onSetKitting(item.id, !kitOk)}
        disabled={!routerReleased}
        className={`${btnBase} disabled:opacity-45 disabled:cursor-not-allowed ${
          kitOk
            ? "rl-pill border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
            : "border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60 hover:text-[hsl(var(--foreground))]"
        }`}
        aria-pressed={kitOk}
        data-testid={`mfg-gate-kitting-${item.id}`}
        title={!routerReleased
          ? "Release the route card first"
          : kitOk
            ? `Kitting complete${item.kitting_complete_by ? " · by " + item.kitting_complete_by : ""} — click to un-confirm`
            : "Confirm kitting complete"
        }
      >
        <span className={iconSlot}>
          <KittingTrayIcon size={14} state={kitOk ? "complete" : "pending"} />
        </span>
        <span className="truncate">{kitOk ? "Kitting complete" : "Kitting pending"}</span>
      </button>
      )}

      {/* Router Card dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[420px] max-w-[92vw]">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold flex items-center gap-2">
              <RouterCardIcon size={16} />
              {routerReleased ? "Update Route Cards" : "Release Route Card"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[hsl(var(--muted-foreground))]">
              One card per line. Add a short label if the item needs several cards — e.g. which sub-assembly each card covers.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <div className="grid grid-cols-[minmax(0,1fr)_120px_24px] gap-x-2 gap-y-1.5 items-center">
              <span className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Route Card #</span>
              <span className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Label <span className="font-normal normal-case tracking-normal">(optional)</span></span>
              <span />
              {draftCards.map((c, idx) => (
                <React.Fragment key={idx}>
                  <Input
                    value={c.num}
                    onChange={(e) => setCard(idx, { num: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRouter(); }}
                    placeholder="e.g. RP.BH.RC.26390"
                    autoFocus={idx === draftCards.length - 1}
                    className="text-sm font-mono-data h-8"
                    data-testid={idx === 0 ? `mfg-gate-router-input-${item.id}` : `mfg-gate-router-input-${item.id}-${idx}`}
                  />
                  <Input
                    value={c.label}
                    onChange={(e) => setCard(idx, { label: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRouter(); }}
                    placeholder="e.g. Bottom Sub"
                    className="text-xs h-8"
                    data-testid={`mfg-gate-router-label-${item.id}-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeCard(idx)}
                    disabled={draftCards.length === 1}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full rl-well text-[hsl(var(--muted-foreground))]/50 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Remove this card"
                    data-testid={`mfg-gate-router-remove-${item.id}-${idx}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </React.Fragment>
              ))}
            </div>
            <button
              type="button"
              onClick={addCard}
              className="mt-2.5 inline-flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))]/80 border border-dashed border-[hsl(var(--border))] rounded-full px-2.5 py-0.5 hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 transition-colors"
              data-testid={`mfg-gate-router-add-${item.id}`}
            >
              <Plus className="w-3 h-3" /> Add another card
            </button>
            {routerReleased && (
              <p className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                Released {item.router_released_by ? `by ${item.router_released_by}` : ""}
                {item.router_released_at ? ` · ${formatDateDMY(item.router_released_at)}` : ""}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 pt-4 mt-2 border-t border-[hsl(var(--border))]/60">
            {routerReleased ? (
              <button
                type="button"
                onClick={clearRouter}
                className="text-[11px] font-semibold text-red-600 hover:text-red-500 transition-colors"
                data-testid={`mfg-gate-router-clear-${item.id}`}
              >
                Clear release
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="h-8 px-3 text-xs rounded-md border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRouter}
                className="h-8 px-3 text-xs font-semibold rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                data-testid={`mfg-gate-router-save-${item.id}`}
              >
                {(() => {
                  const n = draftCards.filter(c => (c.num || "").trim()).length;
                  const noun = n > 1 ? `${n} cards` : "card";
                  return routerReleased ? `Update ${n > 1 ? noun : ""}`.trim() : `Release ${noun}`;
                })()}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdvanceButton({ item, stage, advanceable, isAnimating, lockReason, stageColor, onAdvanceClick, isCompact }) {
  const lustre = useAdvanceLustre(item, stage, advanceable);
  const locked = !advanceable && !!lockReason;
  // Controlled so the reason tooltip can ALSO open on tap (touch has no hover).
  // A disabled <button> doesn't emit pointer events, so the wrapping span
  // carries the tap toggle. Hover/focus still drive it via onOpenChange.
  const [reasonOpen, setReasonOpen] = useState(false);
  const tapToggle = locked
    ? (e) => { e.preventDefault(); e.stopPropagation(); setReasonOpen(o => !o); }
    : undefined;
  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip open={locked ? reasonOpen : undefined} onOpenChange={locked ? setReasonOpen : undefined}>
        <TooltipTrigger asChild>
          {/* span wrapper so the tooltip has a pointer-eventful trigger even
              when the inner button is disabled (lets tap open it on touch). */}
          <span className="inline-flex" onClick={tapToggle}>
            <button
              onClick={() => onAdvanceClick(item)}
              disabled={!advanceable || isAnimating}
              className={`flex items-center gap-1 rounded-full font-bold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${isCompact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"} ${advanceable ? "text-white hover:brightness-110 rl-lit-btn" : "cursor-not-allowed text-[hsl(var(--muted-foreground))]/60 bg-transparent border border-[hsl(var(--border))]"} ${lustre === "full" ? "advance-lustre" : lustre === "breathe" ? "advance-lustre advance-lustre--breathe" : ""}`}
              style={advanceable ? { backgroundColor: stageColor } : {}}
              aria-label={stage === "PO Received" ? "Accept order" : (locked ? `Locked — ${lockReason}` : "Advance to next stage")}
              data-testid={`advance-btn-${item.id}`}
            >
              {stage === "PO Received" ? (
                advanceable
                  ? <><ClipboardCheck className="w-3 h-3" /><span>Accept</span></>
                  : <><span>Accept</span><Lock className="w-3 h-3" /></>
              ) : stage === "Dispatch" ? (
                advanceable
                  ? <><CheckCheck className="w-3.5 h-3.5" /><span>Delivered</span></>
                  : <><span>Delivered</span><Lock className="w-3 h-3" /></>
              ) : (
                <><span>Advance</span>{advanceable ? <ArrowRight className="w-3 h-3" /> : <Lock className="w-3 h-3" />}</>
              )}
            </button>
          </span>
        </TooltipTrigger>
        {locked && (
          <TooltipContent
            side="top"
            sideOffset={6}
            className="bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] shadow-sm text-[11px] font-medium tracking-wide px-2.5 py-1"
            data-testid={`advance-lock-reason-${item.id}`}
          >
            {lockReason}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export const StageRowDesktop = React.memo(
function StageRowDesktopInner({
    item,
    stage,
    stageColor,
    isLastStage,
    isFirstStage,
    canEdit,
    canDelete,
    showValue,
    showSpend,
    partDuplicateMap,
    navigate,
    clientColor,
    clientLogoSvg,
    isAnimating,
    isConfirmingDelete,
    advanceable,
    lockReason,
    onAdvanceClick,
    onReturnClick,
    onReturnToCustomer,
    onBlockClick,
    onUnblock,
    onEditClick,
    onDeleteClick,
    onDetailsClick,
    onProcStatusChange,
    onOpenSourcing,
    onOpenBom,
    onBuildTypeChange,
    canDefineBom,
    canBuyBom,
    onMfgStatusChange,
    onSetRouter,
    onSetKitting,
    onQcStatusChange,
    onQdToggle,
    onCustomerAcceptanceToggle,
    onDispatchStatusChange,
    onSpendClick,
    onNextActionClick,
    onErpClick,
    erpHighlight,
    isAdmin,
    canErpEdit,
    densityCfg,
  }) {
    // Division lens — chips render only in the All lens (see MULTI_DIVISION_PLAN §1b).
    const { lens: divisionLens } = useContext(DivisionLensContext) || { lens: "all" };
    // Per-division pipeline config (Phase 1b): status vocabularies, sub-teams
    // and gates for THIS item's division. Metallic mirrors historic behaviour.
    const { config: divCfg } = useContext(DivisionConfigContext) || {};
    const { updateStatus: ctxUpdateStatus, setDesignApproved } = useContext(DataActionsContext) || {};
    // Owner-first blocks — one chip per open block (plan §6.6). Empty until the
    // backend runs BLOCKS_V2, so the legacy single-reason line stays put.
    const { catalogue: blockCat } = useBlockCatalogue();
    const itemBlocks = openBlocks(item);
    const itemDiv = itemDivision(item);
    const procCfgArea   = divArea(divCfg, itemDiv, "procurement");
    const procSubteams  = procCfgArea.subteams || ["Material", "Tooling", "Gauging"];
    const showSpecialRemark = !!procCfgArea.special_remark;
    const procStatuses  = divStatusOptions(divCfg, itemDiv, "procurement");
    const mfgStatuses   = divStatusOptions(divCfg, itemDiv, "manufacturing");
    const qcStatuses    = divStatusOptions(divCfg, itemDiv, "qc");
    const dispStatuses  = divStatusOptions(divCfg, itemDiv, "dispatch");
    const mfgTerminal   = divTerminalKey(divCfg, itemDiv, "manufacturing", "Machining Complete");
    const mfgRouterGate = divFlag(divCfg, itemDiv, "manufacturing", "router_gate", true);
    const mfgKitGate    = divFlag(divCfg, itemDiv, "manufacturing", "kitting_gate", true);
    const qcShowQd      = divFlag(divCfg, itemDiv, "qc", "qd", true);
    const qcShowCa      = divFlag(divCfg, itemDiv, "qc", "customer_acceptance", true);
    const qcCocGate     = divFlag(divCfg, itemDiv, "qc", "coc_gate", false);
    const showTrFields  = divFlag(divCfg, itemDiv, "dispatch", "tr_fields", true);
    const designGate    = divFlag(divCfg, itemDiv, "po_received", "design_approval_gate", false);
    const dLabel = (area, key) => divStatusLabel(divCfg, itemDiv, area, key);
    // Special-remark expand state (Phase 1b) — collapsed link by default.
    const [remarkOpen, setRemarkOpen] = useState(false);
    const effectiveDueDate = item.dispatch_due_date || item.due_date;
    const descMaxW = densityCfg?.descMaxW || "max-w-[300px]";
    const descFont = densityCfg?.descFont || "text-[13px]";
    const isCompact = !!densityCfg?.isCompact;
    // Compact density: halve the vertical cell padding so rows actually shrink
    // (comfortable keeps px-3 py-3). Horizontal padding is unchanged so columns
    // stay aligned. Applied to every <td> via cellPad / cellPadTight.
    const cellPad = isCompact ? "px-3 py-1.5" : "px-3 py-3";
    const cellPadX2 = isCompact ? "px-2 py-1.5" : "px-2 py-3";
    const partChipPad = isCompact ? "px-1.5 py-[1px]" : "px-2 py-0.5";
    const totalSpend = (item.material_spend || 0) + (item.tooling_spend || 0) + (item.gauging_spend || 0);
    const partNumStr = (item.part_number || "").trim();
    const poNumStr   = (item.po_number || "").trim();
    // Length thresholds tuned for the 160-240px column.
    const partIsLong = partNumStr.length > 11;
    const poIsLong   = poNumStr.length > 11;
    // Tailwind class tokens — pick based on isCompact AND length.
    const partChipFontCls = isCompact
      ? (partIsLong ? "text-[10px]" : "text-[11px]")
      : (partIsLong ? "text-[12px]" : "text-[13px]");
    const poRowFontCls = isCompact
      ? (poIsLong ? "text-[9px]"  : "text-[10px]")
      : (poIsLong ? "text-[10px]" : "text-[11px]");
    // Spend is always rendered under the Value column on Procurement (used to be compact-only).
    const showSpendInValueCol = stage === "Procurement" && showSpend;
    const showValueCol = showValue || showSpendInValueCol;

    const duplicateOthers = (() => {
      if (!partNumStr || !partDuplicateMap) return null;
      const allOcc = partDuplicateMap.get(partNumStr);
      if (!allOcc) return null;
      return allOcc.filter(o => o.id !== item.id);
    })();
    const hasDuplicates = duplicateOthers?.length > 0;
    // Distinct OTHER stages this part is in — the planning signal. Ordered by
    // pipeline position so the "also in" pills read left→right like the flow.
    const dupStages = hasDuplicates
      ? [...new Set(duplicateOthers.map(o => o.stage))].sort(
          (a, b) => STAGE_ORDER.indexOf(a) - STAGE_ORDER.indexOf(b)
        )
      : [];
    // Expedited parts get a premium GOLD treatment across the row (rl-expedite-row
    // = gold left-rail + faint gold wash) plus gold-tinted name + part-number, so
    // priority reads at a glance. Blocked rows keep their own red treatment.
    const isExpedite = item.priority === "Expedite" && !item.is_blocked;
    // Double state: an expedited part that is also blocked — the most urgent
    // thing on the floor. Blocked wins the ROW tint (rose); this drives a single
    // prominent "PRIORITY BLOCKED" tag instead of two competing badges.
    const isPriorityBlocked = item.priority === "Expedite" && item.is_blocked;

    return (
      <tr
        id={`row-${item.id}`}
        className={`item-row group/row border-b border-[hsl(var(--border))]/50 hover:bg-[hsl(var(--muted))]/20 ${isExpedite ? "rl-expedite-row" : ""} ${item.is_blocked ? "blocked-row" : ""} ${isConfirmingDelete ? "bg-red-500/5" : ""} ${isAnimating ? "item-row-advancing" : ""}`}
        data-testid={`item-row-${item.id}`}
      >
        {/* Item Description */}
        <td className={`desc-cell ${cellPad}`}>
          <div className="desc-stack space-y-0.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Division glyph hint (nav polish, 14-Jul) — monochrome, muted,
                  tooltip on hover. PDC bit face (filled) for drilling, hex
                  (stroked) for metallic. All lens only; fixed slot, no reflow. */}
              {divisionLens === "all" && (
                <span
                  className="inline-flex items-center justify-center shrink-0 text-[hsl(var(--muted-foreground))]/60"
                  title={`${divisionLabel(itemDivision(item))} division`}
                  data-testid={`division-glyph-${item.id}`}
                >
                  <DivisionGlyph division={itemDivision(item)} size={12} withTitle={false} />
                </span>
              )}
              <span className="inline-flex items-center gap-1" style={{ color: clientColor }}>
                {clientLogoSvg ? (
                  <span
                    className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
                    style={{ width: 14, height: 14 }}
                    dangerouslySetInnerHTML={{ __html: clientLogoSvg }}
                  />
                ) : (
                  <span
                    className="inline-flex items-center justify-center shrink-0 rounded-full text-[6px] font-bold leading-none"
                    style={{ width: 14, height: 14, color: clientColor, backgroundColor: clientColor + "20" }}
                  >
                    {item.client.split(" ").slice(0, 2).map(w => w[0]).join("")}
                  </span>
                )}
                <span className={`${isCompact ? "text-[11px]" : "text-[12px]"} font-semibold`}>{item.client}</span>
              </span>
              {item.is_qual && (
                /* Ghost orange — a standing attribute like Expedite, so it wears
                   the ghost-badge treatment (no fill, orange text + hairline) in
                   its own colour, matching the Expedite/Blocked badge family. */
                <span
                  className="stage-badge rl-glass-hairline rl-edge-orange shrink-0 inline-flex items-center text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded text-orange-600 dark:text-orange-400"
                  data-testid={`qual-badge-${item.id}`}
                >
                  QUAL
                </span>
              )}
              {/* Priority / blocked marker by the client name. Double state
                  (expedited + blocked) → one prominent solid "PRIORITY BLOCKED"
                  wordmark with a gold bolt; blocked-only → ghost rose BLOCKED;
                  expedite-only → ghost gold EXPEDITE. */}
              {isPriorityBlocked ? (
                <span className="stage-badge rl-priority-blocked-badge shrink-0 inline-flex items-center gap-1 text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded" data-testid={`priority-blocked-badge-${item.id}`}>
                  <Zap className="w-2 h-2 rl-pb-bolt" />Priority Blocked
                </span>
              ) : item.is_blocked ? (
                <span className="stage-badge rl-blocked-badge shrink-0 inline-flex items-center gap-1 text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded" data-testid={`blocked-badge-${item.id}`}>
                  <ShieldAlert className="w-2 h-2" />Blocked
                </span>
              ) : isExpedite ? (
                <span className="stage-badge rl-gold-badge shrink-0 inline-flex items-center gap-0.5 text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded" data-testid={`expedite-badge-${item.id}`}>
                  <Zap className="w-2 h-2" />Expedite
                </span>
              ) : null}
            </div>
            {stage === "Quality Control" && item.router_card_number && (
              <div className="inline-flex flex-col items-start gap-1" data-testid={`rc-display-${item.id}`}>
                {parseRouteCards(item.router_card_number).map((c, i) => (
                  /* Read-only route-card DISPLAY (the editable one is the gate
                     on Manufacturing). A CONTAINED emerald tag — soft border +
                     fill, rounded-md (data shape, not a pill), and NO rl-pill
                     lift/shadow — so it's a clear, noticeable chip that still
                     doesn't read as a pressable button. Leading "RC:" label. */
                  <div key={i} className={`inline-flex items-center rounded-md bg-emerald-500/10 rl-soft-glass max-w-full ${isCompact ? "gap-1 px-1.5 py-0.5" : "gap-1.5 px-2 py-1"}`}>
                    <RouterCardIcon size={isCompact ? 11 : 13} state="complete" />
                    <span className={`font-mono-data font-semibold tracking-wide truncate ${isCompact ? "text-[10px]" : "text-[11px]"}`}>
                      <span className="font-sans font-normal text-emerald-700/60 dark:text-emerald-400/60">RC&nbsp;</span>
                      <span className="text-emerald-700 dark:text-emerald-400">{c.num}</span>
                    </span>
                    {c.label && <span className={`text-emerald-700/60 dark:text-emerald-400/60 truncate ${isCompact ? "text-[9px]" : "text-[10px]"}`}>{c.label}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5 flex-wrap">
              {item.new_to_stage && (
                <>
                  <FreshDot label="New in stage" className="shrink-0" />
                </>
              )}
              <span className={`item-desc font-medium leading-snug break-words ${descFont} ${descMaxW} ${item.is_blocked ? "rl-blocked-text font-semibold" : isExpedite ? "rl-expedite-text font-semibold" : "text-[hsl(var(--foreground))]"}`}>{item.item_description}</span>
              {item.is_rework && (
                <span className="stage-badge shrink-0 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-teal-500/15 text-teal-500 rl-soft-glass" data-testid={`rework-badge-${item.id}`}>
                  <ReworkIcon className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />Rework
                </span>
              )}
              {item.qc_failed_return && (
                <span className="stage-badge shrink-0 flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30" data-testid={`qc-return-badge-${item.id}`} title={item.return_note || "Failed QC — returned to customer"}>
                  <PackageX className="w-2.5 h-2.5" />QC-failed return
                </span>
              )}
              {STAGE_ORDER.indexOf(stage) > STAGE_ORDER.indexOf("Procurement") && (
                <PartialProcuredTag item={item} onOpen={onOpenBom} />
              )}
            </div>
            {/* Assemblies — PO Received decides Single part vs Assembly; an
                assembly gets its child-parts button right beside the switch. */}
            {stage === "PO Received" && (canEdit || isAssembly(item)) && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1" data-testid={`build-row-${item.id}`}>
                <BuildTypeToggle
                  item={item}
                  canEdit={canEdit}
                  isCompact={isCompact}
                  lockedReason={(() => {
                    const n = (item.bom || []).filter(p => p.status !== "Pending").length;
                    return n ? `${n} child part${n === 1 ? "" : "s"} already ordered — it has to stay an assembly` : "";
                  })()}
                  onChange={onBuildTypeChange}
                />
                {isAssembly(item) && <ChildPartsPill item={item} onOpen={onOpenBom} isCompact={isCompact} canAdd={!!canDefineBom} />}
              </div>
            )}
            {item.qc_failed_return && (item.return_note || !showValue) && (
              <div className="flex items-start gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5" data-testid={`qc-return-note-${item.id}`}>
                <PackageX className="w-3 h-3 shrink-0 mt-0.5 text-amber-500" />
                <span className="break-words">
                  {item.return_note}
                  {!showValue && typeof item.return_value_usd === "number" && (
                    <span className="font-mono-data text-amber-700 dark:text-amber-400">{item.return_note ? " · " : ""}return @ {formatCurrency(item.return_value_usd)}</span>
                  )}
                </span>
              </div>
            )}
            {item.is_blocked && (
              <div className="flex flex-col gap-1">
                {/* Owner chips — one per open block, amber outside / slate inside.
                    Multiple chips = multiple dependencies; each is cleared on its
                    own from the modal. Hover carries reason, party, age, promise. */}
                {itemBlocks.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid={`block-chips-${item.id}`}>
                    <ShieldAlert className="w-3 h-3 shrink-0 rl-blocked-reason" />
                    {itemBlocks.map((b) => {
                      const ext = b.owner_type === "external";
                      const ps = promiseState(b);
                      const days = blockAgeDays(b);
                      const head = blockHeadline(blockCat, b);
                      const om = blockCat?.owners?.[b.owner];
                      const tip = [
                        `${om?.label || b.owner || "No owner yet — migrated from the old codes"}${b.party && b.party !== om?.label ? ` — ${b.party}` : ""}`,
                        head,
                        b.note && b.note !== head ? b.note : null,
                        days === null ? null : `open ${days === 0 ? "since today" : `${days} day${days === 1 ? "" : "s"}`}`,
                        b.expected_unblock_at ? `promised ${b.expected_unblock_at}${ps === "overdue" ? " — OVERDUE" : ""}` : null,
                      ].filter(Boolean).join(" · ");
                      return (
                        <button
                          key={b.id}
                          type="button"
                          title={tip}
                          onClick={(e) => { e.stopPropagation(); onBlockClick && onBlockClick(item); }}
                          className={`rl-pill inline-flex items-center gap-1 text-[10.5px] font-medium rounded-full pl-1.5 pr-2 py-px border transition-colors max-w-[280px] ${
                            !b.owner
                              ? "bg-[hsl(var(--muted))]/60 border-dashed border-[hsl(var(--muted-foreground))]/50 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--foreground))]/40"
                              : ext
                              ? "bg-amber-500/10 border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20"
                              : "bg-slate-500/10 border-slate-500/40 text-slate-700 dark:text-slate-200 hover:bg-slate-500/20"
                          }`}
                          data-testid={`block-chip-${b.id}`}
                        >
                          <span className="font-mono-data font-bold text-[9.5px] tracking-wide whitespace-nowrap">{b.owner || "SET OWNER"}</span>
                          <span className="truncate">{head}</span>
                          {b.expected_unblock_at && (
                            <span className={`font-mono-data text-[9.5px] whitespace-nowrap ${ps === "overdue" ? "text-red-500 font-bold" : "opacity-70"}`}>
                              {ps === "overdue" ? "· overdue" : `· ${b.expected_unblock_at.slice(5)}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {Array.isArray(item.block_waiting_on) && item.block_waiting_on.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-orange-700 dark:text-orange-400 bg-orange-500/5 border border-orange-500/40 rounded-full px-2 py-px rl-pill whitespace-nowrap" data-testid={`block-waiting-on-${item.id}`}>
                        <Hourglass className="w-2.5 h-2.5 shrink-0" />
                        Waiting on {item.block_waiting_on.join(", ")}
                      </span>
                    )}
                  </div>
                )}
                <div className={`flex items-center gap-1.5 text-[11px] rl-blocked-reason flex-wrap ${itemBlocks.length > 0 ? "hidden" : ""}`}>
                  <ShieldAlert className="w-3 h-3 shrink-0" />
                  {item.block_reason_code && (
                    <span className="font-mono-data font-bold rl-blocked-code text-[10px]">{item.block_reason_code}</span>
                  )}
                  <span>{item.block_reason}</span>
                  {Array.isArray(item.block_waiting_on) && item.block_waiting_on.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-orange-700 dark:text-orange-400 bg-orange-500/5 border border-orange-500/40 rounded-full px-2 py-px rl-pill whitespace-nowrap" data-testid={`block-waiting-on-${item.id}`}>
                      <Hourglass className="w-2.5 h-2.5 shrink-0" />
                      Waiting on {item.block_waiting_on.join(", ")}
                    </span>
                  )}
                  {item.block_next_action_demanded && !item.next_action && Array.isArray(item.block_waiting_on) && item.block_waiting_on.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-amber-700 dark:text-amber-400 border border-dashed border-amber-500/60 rounded-full px-2 py-px whitespace-nowrap">
                      <Timer className="w-2.5 h-2.5 shrink-0" /> next action awaited
                    </span>
                  )}
                </div>
                {/* Next-action plan — owner accountability */}
                {item.next_action ? (
                  /* "Next" plan — amber (the app's action/plan colour, distinct
                     from the rose problem). Left-accent tick + faint fill, no
                     full box, so it flows with the calm blocked row and echoes
                     its rail motif. Pencil edit fades in on hover. */
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onNextActionClick && onNextActionClick(item); }}
                    className="rl-na-chip group/na flex items-start gap-2 text-left text-[11px] pl-2 pr-2.5 py-1 rounded-r-md bg-amber-500/5 hover:bg-amber-500/10 border-l-2 border-amber-500/70 transition-colors max-w-[400px]"
                    data-testid={`next-action-chip-${item.id}`}
                    title="Click to edit next action"
                  >
                    <RotateCcw className="w-3 h-3 shrink-0 text-amber-600 mt-[1px] transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover/na:-rotate-[270deg]" />
                    <span className="flex-1 min-w-0">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mr-1.5">Next</span>
                      <span className="text-[hsl(var(--foreground))] break-words">{item.next_action}</span>
                      <span className="text-[hsl(var(--muted-foreground))] font-mono-data ml-1.5 whitespace-nowrap">· by {item.next_action_date}</span>
                      {item.next_action_by && (
                        <span className="block text-[9px] text-[hsl(var(--muted-foreground))] mt-0.5">
                          Owned by <span className="font-semibold text-[hsl(var(--foreground))]">{item.next_action_by}</span>{item.next_action_by_role && ` (${item.next_action_by_role})`}
                        </span>
                      )}
                    </span>
                    <Pencil className="w-2.5 h-2.5 text-amber-600/50 opacity-0 group-hover/na:opacity-100 transition-opacity shrink-0 mt-0.5" />
                  </button>
                ) : (
                  onNextActionClick && (
                    /* Hover-reveal affordance: a reserved fixed-height slot (so the
                       row never shakes) that fades in a soft amber ghost pill on
                       row hover; its ↻ icon spins on hover. Matches the filled
                       next-action chip's language, not a cheap caps link. */
                    <div className="self-start h-[22px] flex items-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onNextActionClick(item); }}
                        className="rl-set-na group/setna inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-md border border-dashed border-amber-500/40 text-amber-700 dark:text-amber-400 opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto group-focus-within/row:opacity-100 group-focus-within/row:pointer-events-auto hover:bg-amber-500/10 hover:border-amber-500/60 focus-visible:opacity-100 focus-visible:pointer-events-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 transition-[opacity,background-color,border-color] duration-200"
                        data-testid={`set-next-action-${item.id}`}
                      >
                        <RotateCcw className="w-3 h-3 shrink-0 transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover/setna:-rotate-[270deg]" />
                        Specify next action
                      </button>
                    </div>
                  )
                )}
              </div>
            )}
            {item.return_reason && !item.is_blocked && (
              <div className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                <ArrowLeft className="w-3 h-3 shrink-0" />
                <span className="italic">{item.return_reason}</span>
              </div>
            )}
            {Array.isArray(item.drawing_links) && item.drawing_links.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-[hsl(var(--muted-foreground))] pt-0.5" data-testid={`drawing-links-${item.id}`}>
                <Link2 className="w-3 h-3 text-amber-500 shrink-0" />
                <span className="font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Drawings</span>
                {item.drawing_links.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="font-mono-data px-1 py-0 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 hover:underline transition-colors max-w-[180px] truncate"
                    title={url}
                    data-testid={`drawing-link-${item.id}-${i}`}
                  >
                    #{i + 1}
                  </a>
                ))}
              </div>
            )}
            {item.stage_comment && !item.is_blocked && !item.return_reason && (
              <div className={`text-[11px] ${isExpedite ? "rl-expedite-comment" : "text-[hsl(var(--muted-foreground))]"}`}>{item.stage_comment}</div>
            )}
            {stage === "Manufacturing" && (item.machine_used || item.cycle_time) && (
              <div className="flex items-center gap-2 text-[10px] text-[hsl(var(--muted-foreground))] pt-0.5">
                {item.machine_used && <span className="flex items-center gap-1"><Wrench className="w-2.5 h-2.5" />{item.machine_used}</span>}
                {item.cycle_time && <span>· {item.cycle_time}</span>}
              </div>
            )}
            {(stage === "Dispatch" || stage === "Completed") && (item.carrier_name || item.tracking_id) && (
              <div className="flex items-center gap-1.5 pt-0.5" data-testid={`tracking-${item.id}`}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 100" width={isCompact ? 26 : 32} height={isCompact ? 13 : 16} aria-hidden="true" style={{ flexShrink: 0 }}>
                  <path d="M 15 80 L 15 25 C 15 22 17 20 20 20 L 110 20 C 120 20 130 25 135 35 L 145 50 L 160 55 C 165 56 170 60 170 70 L 170 80 L 145 80 A 15 15 0 0 1 115 80 L 65 80 A 15 15 0 0 1 35 80 L 15 80 Z" fill="#FFC400"/>
                  <polygon points="115,22 133,38 143,50 115,50" fill="#555555"/>
                  <path d="M 150 52 L 163 55 C 167 56 170 60 170 70 L 170 80 L 145 80 A 15 15 0 0 1 127 70 Z" fill="#333333"/>
                  <rect x="13" y="70" width="5" height="10" rx="1" fill="#333333"/>
                  <g fill="#D40511">
                    <rect x="20" y="38" width="8" height="1.5"/><rect x="20" y="41" width="8" height="1.5"/><rect x="20" y="44" width="8" height="1.5"/>
                    <polygon points="28,46 38,36 48,36 38,46"/><polygon points="43,46 53,36 63,36 53,46"/>
                    <polygon points="58,46 68,36 78,36 68,46"/><polygon points="73,46 83,36 93,36 83,46"/>
                    <rect x="88" y="38" width="8" height="1.5"/><rect x="88" y="41" width="8" height="1.5"/><rect x="88" y="44" width="8" height="1.5"/>
                    <rect x="25" y="52" width="70" height="3"/>
                  </g>
                  <circle cx="50" cy="80" r="12" fill="#222222"/><circle cx="50" cy="80" r="6" fill="#CCCCCC"/><circle cx="50" cy="80" r="2" fill="#222222"/>
                  <circle cx="130" cy="80" r="12" fill="#222222"/><circle cx="130" cy="80" r="6" fill="#CCCCCC"/><circle cx="130" cy="80" r="2" fill="#222222"/>
                </svg>
                {item.carrier_name && (
                  <span className={`font-semibold text-[hsl(var(--muted-foreground))] ${isCompact ? "text-[9px]" : "text-[10px]"}`}>{item.carrier_name}</span>
                )}
                {item.tracking_id && (() => {
                  const trackUrl = carrierTrackingUrl(item.carrier_name, item.tracking_id);
                  // Known carrier → the number pill IS the track link: on row
                  // hover it expands a touch, the number underlines, and a small
                  // arrow fades in (see App.css .rl-track-arrow for the gentle,
                  // hover-only nudge). Unknown carrier → a plain static pill,
                  // not clickable (never a dead link). Opens via the web-app-
                  // safe opener.
                  const pillBase = "rl-track-pill group/track inline-flex items-center font-mono-data text-[10px] font-bold rounded-full rl-pill bg-amber-500/5 border border-amber-500/40 text-amber-700 dark:text-amber-400 leading-none transition-[padding,background-color] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]";
                  if (!trackUrl) {
                    return (
                      <span className={`${pillBase} px-2 py-0.5`}>{item.tracking_id}</span>
                    );
                  }
                  return (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openTrackingUrl(trackUrl); }}
                      className={`${pillBase} px-2 py-0.5 group-hover/row:px-2.5 group-hover/row:bg-amber-500/10 cursor-pointer hover:bg-amber-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50`}
                      title={`Track ${item.carrier_name} shipment ${item.tracking_id}`}
                      data-testid={`track-btn-${item.id}`}
                    >
                      <span className="underline decoration-transparent decoration-dotted underline-offset-2 group-hover/row:decoration-amber-500/70 transition-[text-decoration-color] duration-200">
                        {item.tracking_id}
                      </span>
                      {/* The arrow is ALWAYS shown (when a link exists) so
                          trackable numbers stand apart at a glance. The "Track"
                          word slides out on row hover (like the edit pencil) for
                          the full affordance. Same 10px size + baseline as the
                          number so the unit reads evenly. */}
                      <span className="inline-flex items-center max-w-0 overflow-hidden opacity-0 group-hover/row:max-w-[44px] group-hover/row:opacity-100 group-hover/row:ml-1.5 group-hover/row:mr-0.5 transition-[max-width,opacity,margin] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]">
                        <span className="text-[10px] font-bold leading-none">Track</span>
                      </span>
                      <ExternalLink className="w-2.5 h-2.5 shrink-0 ml-1 rl-track-arrow" aria-hidden="true" />
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
        </td>

        {/* Part # / Rev */}
        <td className={`${cellPad} align-middle min-w-[130px] max-w-[200px]`}>
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
                      {item.part_number ? (
                        /* Carved (recessed) — NOT rl-pill. A part number is
                           read-only data, so it wears the inset "data field"
                           treatment, not the lifted/pressable button grammar
                           that made it look clickable. */
                        <span className={`font-mono-data ${partChipFontCls} ${partChipPad} rounded-md rl-carved inline-block max-w-full [overflow-wrap:anywhere] [word-break:break-word] ${item.is_blocked ? "rl-blocked-chip font-bold" : isExpedite ? "rl-expedite-chip font-bold" : "bg-[hsl(var(--muted))]/40 text-[hsl(var(--foreground))] font-semibold"}`}>{item.part_number}</span>
                      ) : (
                        <span className={`font-mono-data ${partChipFontCls} ${partChipPad} rounded-md border border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] opacity-60 inline-block max-w-full [overflow-wrap:anywhere] [word-break:break-word]`}>No Part #</span>
                      )}
                      {item.revision && (
                        <span className={`font-mono-data ${partChipFontCls} ${partChipPad} rounded-md rl-carved ${item.is_blocked ? "rl-blocked-chip" : isExpedite ? "rl-expedite-chip" : "bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]"}`} title={`Revision ${item.revision}`}>{item.revision}</span>
                      )}
                      {hasDuplicates && (
                        <AlsoInMarker
                          item={item}
                          duplicateOthers={duplicateOthers}
                          dupStages={dupStages}
                          partNumStr={partNumStr}
                          navigate={navigate}
                        />
                      )}
                    </div>
                    <div className={`grid grid-cols-[18px_minmax(0,1fr)] gap-x-1.5 gap-y-0.5 items-baseline ${poRowFontCls} font-mono-data leading-tight ${item.is_blocked ? "rl-blocked-meta" : isExpedite ? "rl-expedite-meta" : "text-[hsl(var(--foreground))]/85"}`}>
                      <span className={`text-right text-[8px] font-bold uppercase tracking-wide leading-none ${(isExpedite || item.is_blocked) ? "" : "text-[hsl(var(--muted-foreground))]/70"}`}>PO</span>
                      <span className="font-semibold [overflow-wrap:anywhere]">{item.po_number}</span>
                      {item.line_number && (
                        <>
                          <span className={`text-right text-[8px] font-bold uppercase tracking-wide leading-none ${(isExpedite || item.is_blocked) ? "" : "text-[hsl(var(--muted-foreground))]/70"}`}>LN</span>
                          <span className={(isExpedite || item.is_blocked) ? "" : "text-[hsl(var(--foreground))]/70"}>{item.line_number}</span>
                        </>
                      )}
                    </div>
                    {ERP_VISIBLE_STAGES.has(stage) && (
                      <ErpCodeChip
                        item={item}
                        stage={stage}
                        canErpEdit={canErpEdit}
                        isAdmin={isAdmin}
                        onErpClick={onErpClick}
                        highlight={erpHighlight}
                        isCompact={isCompact}
                      />
                    )}
          </div>
        </td>

        {/* Qty */}
        <td className={`${cellPad} font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} font-semibold whitespace-nowrap text-center`}>{item.quantity}</td>

        {/* Value */}
        {showValueCol && (
          <td className={`${cellPad} text-right font-mono-data whitespace-nowrap align-middle`}>
            <div className="flex flex-col items-end gap-1">
              {showValue && (
                item.qc_failed_return ? (
                  <div className="flex flex-col items-end leading-tight">
                    <span className={`font-mono-data ${isCompact ? "text-[10px]" : "text-[11px]"} text-[hsl(var(--muted-foreground))] line-through`}>{formatCurrency(item.value_usd)}</span>
                    <span className={`font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} font-semibold text-amber-700 dark:text-amber-400`}>{formatCurrency(item.return_value_usd || 0)} return</span>
                  </div>
                ) : (
                  <span className={`font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} font-semibold`}>{formatCurrency(item.value_usd)}</span>
                )
              )}
              {showSpendInValueCol && (
                (() => {
                  const spendTotal = Number(totalSpend) || 0;
                  const valueTotal = Number(item.value_usd) || 0;
                  const pct = valueTotal > 0 ? Math.min(100, (spendTotal / valueTotal) * 100) : 0;
                  const tooltip = spendTotal > 0
                    ? (showValue
                        ? `Spent ${formatCurrency(spendTotal)} of ${formatCurrency(valueTotal)} · ${pct.toFixed(0)}%`
                        : `Spent ${formatCurrency(spendTotal)}`)
                    : "No spend recorded — click to add";

                  if (spendTotal <= 0) {
                    // ZERO state — dashed pill "+ Add spend"
                    return (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSpendClick?.(item); }}
                        title={tooltip}
                        className={`group/spend mt-2 inline-flex items-center gap-1 ${isCompact ? "px-2 py-0.5" : "px-2.5 py-0.5"} rounded-full border border-dashed border-[hsl(var(--border))] ${isCompact ? "text-[9.5px]" : "text-[10.5px]"} font-medium text-[hsl(var(--muted-foreground))] hover:border-emerald-600 hover:bg-emerald-600/10 hover:text-emerald-700 dark:hover:text-emerald-400 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40`}
                        data-testid={`add-spend-${item.id}`}
                      >
                        <Plus className={`${isCompact ? "w-2.5 h-2.5" : "w-3 h-3"}`} />
                        <span>Add spend</span>
                      </button>
                    );
                  }

                  // SPENT state — rail on top, text below; compact-aware sizing
                  return (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onSpendClick?.(item); }}
                      title={tooltip}
                      className="group/spend mt-1.5 flex flex-col items-end gap-1 ml-auto bg-transparent border-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 rounded-sm"
                      data-testid={`edit-spend-${item.id}`}
                    >
                      {showValue && (
                        <div
                          className={`relative ${isCompact ? "w-16" : "w-20"} h-[2.5px] rounded-full bg-emerald-600/20 overflow-hidden`}
                          role="progressbar"
                          aria-valuenow={Math.round(pct)}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className="absolute left-0 top-0 bottom-0 rounded-full bg-emerald-600 transition-all"
                            style={{ width: `${Math.max(pct, 4)}%` }}
                          />
                        </div>
                      )}
                      <span className={`inline-flex items-center gap-1 font-mono-data font-semibold text-emerald-700 dark:text-emerald-400 ${isCompact ? "text-[10px]" : "text-[11px]"}`}>
                        <Pencil
                          className={`opacity-0 -translate-x-0.5 group-hover/row:opacity-70 group-hover/row:translate-x-0 transition-all duration-200 ${isCompact ? "w-2 h-2" : "w-2.5 h-2.5"}`}
                          aria-hidden="true"
                        />
                        <span>{formatCurrency(spendTotal)}</span>
                        <span className={`font-sans font-medium opacity-65 tracking-wide ${isCompact ? "text-[8px]" : "text-[9px]"}`}>SPENT</span>
                      </span>
                    </button>
                  );
                })()
              )}
            </div>
          </td>
        )}

        {/* Dispatch / Due date column.
            Completed stage: plain date (red + "Nd late" tag if shipped after due).
            Other stages:    overdue/remaining relative label. */}
        <td className={`${cellPad} whitespace-nowrap text-center`}>
          {stage === "Completed" ? (
            <CompletedDueCell
              dueDateStr={effectiveDueDate}
              completionDateStr={item?.shipped_at}
            />
          ) : (
            <DispatchDueCell dueDateStr={effectiveDueDate} isDispatchDue={!!item.dispatch_due_date} shippedAt={item.shipped_at} compact={isCompact} />
          )}
        </td>

        {stage === "Dispatch" && (
          <td className={`${cellPadX2} text-center`} data-testid={`dispatch-shipped-on-${item.id}`}>
            {/* Same editable ship-date cell as the Completed stage. A ship date
                only makes sense once the item is Shipped — so it's editable
                only then. For Pending / Pickup Awaited / Negotiating Rates the
                cell is read-only "Unknown" (no pencil, no Set): you can't ship-
                date something that hasn't shipped. */}
            <div className="inline-flex justify-center">
              <CompletedOnCell
                item={item}
                canEdit={canEdit && item.dispatch_status === "Shipped"}
                emptyLabel="Yet to ship"
              />
            </div>
          </td>
        )}

        {/* Shipped On — only shown in the Completed stage */}
        {stage === "Completed" && (
          <td className={`${cellPad} whitespace-nowrap`} data-testid={`shipped-on-${item.id}`}>
            <CompletedOnCell item={item} canEdit={canEdit} />
          </td>
        )}

        {/* Procurement status — comfortable mode shows full subteam labels
            tinted in the team color; compact mode uses dot + abbreviation
            to fit denser rows. Dropdowns trimmed to 124/118px so the column
            still fits the table width. */}
        {stage === "Procurement" && (
          <td className={cellPad}>
            {canEdit ? (
              <div className={isCompact ? "space-y-1" : "space-y-1.5"}>
                {[
                  { label: "Material", short: "R.M.",  field: "material_status", val: item.material_status || "Pending" },
                  { label: "Tooling",  short: "TOOL.", field: "tooling_status",  val: item.tooling_status  || "Pending" },
                  { label: "Gauging",  short: "GAUG.", field: "gauging_status",  val: item.gauging_status  || "Pending" },
                ].filter(({ label }) => procSubteams.includes(label)).map(({ label, short, field, val }) => {
                  const tc = PROC_TEAM_COLORS[label];
                  const etaIso = item[ETA_FIELD[field]];
                  const showEta = val === "Ordered";
                  return (
                    <div key={field} className="flex items-center gap-1.5">
                      {isCompact ? (
                        <span
                          className="inline-flex items-center gap-1 shrink-0 w-[46px]"
                          style={{ color: tc.color }}
                          title={label}
                        >
                          <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tc.color }} aria-hidden="true" />
                          <span className="uppercase font-bold tracking-widest text-[9px]">{short}</span>
                        </span>
                      ) : (
                        <span
                          className="uppercase font-bold tracking-widest shrink-0 text-[9px] w-[58px]"
                          style={{ color: tc.color }}
                        >
                          {label}
                        </span>
                      )}
                      {field === "material_status" && isAssembly(item) ? (
                        /* Assembly: Material follows the Child Part Tracker —
                           a read-only pill (no chevron) beside the parts button. */
                        <>
                          <span
                            className={`inline-flex items-center ${statusTriggerCls(val)} ${statusCtlSize(STATUS_CTL_W.Procurement, isCompact)} cursor-default`}
                            title="Set by the Child Part Tracker — update the parts there"
                            data-testid={`${field}-${item.id}`}
                          >
                            <StatusGlyph status={val} size={STATUS_ICON_PX} className="shrink-0" />
                            <span className="block min-w-0 flex-1 truncate">{dLabel("procurement", val)}</span>
                          </span>
                          <ChildPartsPill item={item} onOpen={onOpenBom} isCompact={isCompact} canAdd={!!canDefineBom} />
                        </>
                      ) : (
                      <Select value={val} onValueChange={(newVal) => onProcStatusChange(item, field, label, newVal)}>
                        <SelectTrigger
                          className={`${statusTriggerCls(val)} ${statusCtlSize(STATUS_CTL_W.Procurement, isCompact)}`}
                          data-testid={`${field}-${item.id}`}
                        >
                          <StatusGlyph status={val} size={STATUS_ICON_PX} className="shrink-0" />
                          <SelectValue><span className="block min-w-0 flex-1 truncate" title={dLabel("procurement", val)}>{dLabel("procurement", val)}</span></SelectValue>
                        </SelectTrigger>
                        <SelectContent className="min-w-[140px]">
                          {(procStatuses.length ? procStatuses : PROCUREMENT_STATUSES.map(s => ({ key: s, label: s }))).map(s => (
                            <SelectItem key={s.key} value={s.key} className="text-xs py-1">
                              <span className="inline-flex items-center gap-1.5">
                                <StatusGlyph status={s.key} size={10} className="shrink-0" />
                                <span style={{ color: (PROC_STATUS_COLORS[s.key] || {}).color }}>{s.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      )}
                      {showEta && !(field === "material_status" && isAssembly(item)) && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[10px] font-mono-data whitespace-nowrap shrink-0"
                          style={{ color: PROC_STATUS_COLORS.Ordered.color, opacity: 0.95 }}
                          data-testid={`${field.replace('_status','')}-eta-${item.id}`}
                          title={`Ordered ETA: ${formatEtaShort(etaIso)}`}
                        >
                          <Timer className="w-2.5 h-2.5 shrink-0 opacity-70" />
                          <span className="font-bold tabular-nums">{formatEtaShort(etaIso)}</span>
                        </span>
                      )}
                      {/* Sourcing pill — appears once material is reserved from
                          stores; reopens the sourcing hub. Doubles as the
                          visible "this line uses stores stock" marker. */}
                      {field === "material_status" && (() => {
                        const nStores = (item.spend_entries || []).filter(e => e.source === "stores" && !e.deleted_at).length;
                        if (!nStores) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => onOpenSourcing?.(item)}
                            className="inline-flex items-center gap-0.5 shrink-0 text-[9px] font-bold px-1.5 py-[3px] rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-colors"
                            title={`${nStores} stores reservation${nStores > 1 ? "s" : ""} on this line — open material sourcing`}
                            data-testid={`sourcing-pill-${item.id}`}
                          >
                            <Warehouse className="w-2.5 h-2.5 shrink-0" />
                            ×{nStores}
                          </button>
                        );
                      })()}
                    </div>
                  );
                })}
                {/* Special Remark (Phase 1b, drilling) — ONE per line, below
                    the sub-team dropdowns. Collapsed = a low-height text link
                    (hover-revealed when empty) so clean rows stay short;
                    expands into an input on click. */}
                {showSpecialRemark && (
                  <div className="pt-0.5 leading-none">
                    {remarkOpen ? (
                      <input
                        type="text"
                        defaultValue={item.special_remark || ""}
                        autoFocus
                        placeholder="Special remark…"
                        maxLength={200}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRemarkOpen(false); }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (item.special_remark || "")) ctxUpdateStatus?.(item.id, "special_remark", v);
                          setRemarkOpen(false);
                        }}
                        className="w-full h-6 px-1.5 text-[10px] rounded border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/20 focus:outline-none focus:ring-1 focus:ring-orange-500/50 placeholder:text-[hsl(var(--muted-foreground))]/50"
                        data-testid={`special-remark-input-${item.id}`}
                      />
                    ) : (item.special_remark || "").trim() ? (
                      <button
                        type="button"
                        onClick={() => setRemarkOpen(true)}
                        className="max-w-full truncate block text-left text-[10px] text-amber-700 dark:text-amber-400 hover:underline"
                        title={`Special remark: ${item.special_remark} — click to edit`}
                        data-testid={`special-remark-${item.id}`}
                      >
                        ✱ {item.special_remark}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setRemarkOpen(true)}
                        className="text-[10px] text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto transition-opacity"
                        data-testid={`special-remark-add-${item.id}`}
                      >
                        + Special remark
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className={isCompact ? "space-y-0.5" : "space-y-1.5"}>
                {[
                  { label: "Material", short: "R.M.",  field: "material_status", val: item.material_status || "Pending" },
                  { label: "Tooling",  short: "TOOL.", field: "tooling_status",  val: item.tooling_status  || "Pending" },
                  { label: "Gauging",  short: "GAUG.", field: "gauging_status",  val: item.gauging_status  || "Pending" },
                ].filter(({ label }) => procSubteams.includes(label)).map(({ label, short, field, val }) => {
                  const etaIso = item[ETA_FIELD[field]];
                  return (
                    <div key={label} className="flex items-start gap-1.5">
                      {isCompact ? (
                        <span
                          className="inline-flex items-center gap-1 shrink-0 w-[46px] mt-0.5"
                          style={{ color: PROC_TEAM_COLORS[label].color }}
                          title={label}
                        >
                          <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: PROC_TEAM_COLORS[label].color }} aria-hidden="true" />
                          <span className="uppercase font-bold tracking-widest text-[9px]">{short}</span>
                        </span>
                      ) : (
                        <span
                          className="uppercase font-bold tracking-widest shrink-0 text-[9px] w-14 mt-0.5"
                          style={{ color: PROC_TEAM_COLORS[label].color }}
                        >
                          {label}
                        </span>
                      )}
                      <div className="flex flex-col gap-0.5 items-start">
                        <ProcStatusBadge status={val} label={dLabel("procurement", val)} />
                        {field === "material_status" && isAssembly(item) && (
                          <ChildPartsPill item={item} onOpen={onOpenBom} isCompact />
                        )}
                        {val === "Ordered" && (
                          <span
                            className="text-[9px] font-mono-data font-bold opacity-80"
                            style={{ color: PROC_TEAM_COLORS[label].color }}
                          >
                            ETA · {formatEtaShort(etaIso)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Special remark — read-only display */}
                {showSpecialRemark && (item.special_remark || "").trim() && (
                  <p className="text-[10px] text-amber-700 dark:text-amber-400 truncate max-w-full" title={item.special_remark}>
                    ✱ {item.special_remark}
                  </p>
                )}
              </div>
            )}
          </td>
        )}

        {/* Manufacturing status + Router/Kitting gate */}
        {stage === "Manufacturing" && (
          <td className={`${cellPad} align-middle`}>
            {canEdit ? (
              (() => {
                // Per-division gates (Phase 1b): drilling disables both router
                // and kitting (Route Card is a status there, not a gate).
                const gateEnabled = !!item.mfg_gate_version && (mfgRouterGate || mfgKitGate);
                const gateOk = !gateEnabled || (
                  (!mfgRouterGate || !!item.router_card_number) &&
                  (!mfgKitGate || !!item.kitting_complete)
                );
                const mfgVal = item.manufacturing_status || (mfgStatuses[0]?.key || "Queued");
                return (
                  <div className={isCompact ? "flex flex-col gap-0.5" : "flex flex-col gap-1"}>
                    {gateEnabled && (
                      <MfgGateToggles
                        item={item}
                        onSetRouter={onSetRouter}
                        onSetKitting={onSetKitting}
                        isCompact={isCompact}
                        showKitting={mfgKitGate}
                      />
                    )}
                    <Select
                      value={mfgVal}
                      onValueChange={(val) => onMfgStatusChange(item.id, val)}
                      disabled={!gateOk}
                    >
                      <SelectTrigger
                        /* [gap:0.5rem] + the w-[18px] icon slot below MATCH the
                           gate pills (MfgGateToggles btnBase gap-2 / iconSlot
                           w-[18px]) so the CNC icon shares a vertical centre-line
                           with the RC/Kitting icons and "Third Party" starts on
                           the same left edge as "KITTING COMPLETE". Overrides the
                           shared gap-1.5 from statusTriggerCls for Mfg only. */
                        className={`${statusTriggerCls(item.manufacturing_status)} ${statusCtlSize(STATUS_CTL_W.Manufacturing, isCompact)} [gap:0.5rem] ${!gateOk ? "opacity-55 cursor-not-allowed" : ""}`}
                        data-testid={`mfg-status-${item.id}`}
                        title={!gateOk ? "Release the route card and confirm kitting first" : undefined}
                      >
                        <span className="inline-flex items-center justify-center w-5 h-[14px] shrink-0">
                          <CncMachineIcon
                            key={mfgVal}
                            size={STATUS_ICON_PX}
                            state={
                              mfgVal === mfgTerminal ? "complete" :
                              mfgVal === "Third Party" ? "third-party" :
                              mfgVal === "Queued" ? "queued" :
                              "processing"
                            }
                          />
                        </span>
                        <SelectValue><span className="block min-w-0 flex-1 truncate" title={dLabel("manufacturing", mfgVal)}>{dLabel("manufacturing", mfgVal)}</span></SelectValue>
                      </SelectTrigger>
                      <SelectContent className="min-w-[140px]">
                        {(mfgStatuses.length ? mfgStatuses : MANUFACTURING_STATUSES.map(s => ({ key: s, label: s }))).map(s => {
                          const mfgState = s.key === mfgTerminal ? "complete" : s.key === "Third Party" ? "third-party" : s.key === "Queued" ? "queued" : "processing";
                          return (
                            <SelectItem key={s.key} value={s.key} className="text-xs py-1">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="inline-flex items-center justify-center w-[14px] h-[14px] shrink-0">
                                  <CncMachineIcon size={12} state={mfgState} />
                                </span>
                                {s.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {/* Third Party info — shown below dropdown when status is set */}
                    {item.manufacturing_status === "Third Party" && (item.third_party_where || item.third_party_eta) && (
                      <div className="flex flex-col gap-0.5 pl-1" style={{ maxWidth: 200 }}>
                        {item.third_party_where && (
                          <span className="flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]" data-testid={`third-party-where-${item.id}`}>
                            <MapPin className="w-2.5 h-2.5 shrink-0 text-amber-500" />
                            {item.third_party_where}
                          </span>
                        )}
                        {item.third_party_eta && (
                          <span className="flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]" data-testid={`third-party-eta-${item.id}`}>
                            <Calendar className="w-2.5 h-2.5 shrink-0 text-amber-500" />
                            ETA: {item.third_party_eta}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-[hsl(var(--muted))]">
                <CncMachineIcon
                  size={13}
                  state={
                    item.manufacturing_status === mfgTerminal ? "complete" :
                    item.manufacturing_status === "Third Party" ? "third-party" :
                    (item.manufacturing_status === "Queued" || !item.manufacturing_status) ? "queued" :
                    "processing"
                  }
                />
                {dLabel("manufacturing", item.manufacturing_status || (mfgStatuses[0]?.key || "Queued"))}
              </span>
            )}
          </td>
        )}

        {/* QC status */}
        {stage === "Quality Control" && (
          <td className={`${cellPad} align-middle`}>
            {canEdit ? (
              <div className={isCompact ? "flex flex-col gap-0.5" : "flex flex-col gap-1"}>
                <Select value={item.qc_status} onValueChange={(val) => onQcStatusChange(item.id, val)}>
                  <SelectTrigger className={`${statusTriggerCls(item.qc_status || "Pending Inspection")} ${statusCtlSize(STATUS_CTL_W["Quality Control"], isCompact)}`} data-testid={`qc-status-${item.id}`}>
                    <StatusGlyph status={item.qc_status} size={STATUS_ICON_PX} className="shrink-0" />
                    <SelectValue><span className="block min-w-0 flex-1 truncate" title={item.qc_status}>{item.qc_status}</span></SelectValue>
                  </SelectTrigger>
                  <SelectContent className="min-w-[140px]">
                    {(qcStatuses.length ? qcStatuses : QC_STATUSES.map(s => ({ key: s, label: s }))).map(s => (
                      <SelectItem key={s.key} value={s.key} className="text-xs py-1">
                        <span className="inline-flex items-center gap-1.5">
                          <StatusGlyph status={s.key} size={10} className="shrink-0" />
                          {s.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {qcShowQd && item.qc_status === "Passed" && (
                  <QualityDossierToggle
                    itemId={item.id}
                    ready={!!item.quality_dossier_ready}
                    onToggle={onQdToggle}
                    isCompact={isCompact}
                  />
                )}
                {/* Customer Acceptance gate — only shown after QD Ready */}
                {qcShowCa && item.qc_status === "Passed" && (
                  <CustomerAcceptanceToggle
                    itemId={item.id}
                    accepted={!!item.customer_acceptance}
                    onToggle={onCustomerAcceptanceToggle}
                    disabled={qcShowQd && !item.quality_dossier_ready}
                    isCompact={isCompact}
                  />
                )}
                {/* COC gate (Phase 1b, drilling) — Certificate of Conformity.
                    Same visual family as the QD/CA toggles: shown after Passed,
                    green when complete, amber-pending otherwise. */}
                {qcCocGate && item.qc_status === "Passed" && (
                  <button
                    type="button"
                    onClick={() => ctxUpdateStatus?.(item.id, "coc_status", item.coc_status === "Complete" ? "Pending" : "Complete")}
                    className={`inline-flex items-center gap-1.5 self-start text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded rl-glass-hairline transition-colors ${
                      item.coc_status === "Complete"
                        ? "bg-green-500/10 text-green-600 rl-edge-green"
                        : "bg-amber-500/10 text-amber-600 rl-edge-amber hover:bg-amber-500/20"
                    }`}
                    title={item.coc_status === "Complete" ? "COC complete — click to reopen" : "Certificate of Conformity pending — click to mark complete"}
                    data-testid={`coc-toggle-${item.id}`}
                  >
                    {item.coc_status === "Complete" ? <CheckCircle2 className="w-3 h-3" /> : <Clock3 className="w-3 h-3" />}
                    COC {item.coc_status === "Complete" ? "Complete" : "Pending"}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-[hsl(var(--muted))]">
                  <StatusGlyph status={item.qc_status} size={11} />
                  {item.qc_status}
                </span>
                {qcShowQd && item.qc_status === "Passed" && item.quality_dossier_ready && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                    <CheckCircle2 className="w-3 h-3" />QD Ready
                  </span>
                )}
                {qcShowCa && item.qc_status === "Passed" && item.customer_acceptance && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                    <SquareCheck className="w-3 h-3" />Cust. Accepted
                  </span>
                )}
                {qcCocGate && item.qc_status === "Passed" && item.coc_status === "Complete" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                    <CheckCircle2 className="w-3 h-3" />COC Complete
                  </span>
                )}
              </div>
            )}
          </td>
        )}

        {/* Dispatch status */}
        {stage === "Dispatch" && (
          <td className={cellPadX2}>
            {canEdit ? (
              <Select value={item.dispatch_status || "Pending"} onValueChange={(val) => onDispatchStatusChange(item, val)}>
                {/* Left-alignment is handled centrally by statusCtlSize (flex-start
                    + chevron ml-auto), so the status text shares a stable left
                    edge with the Ticket/Invoice ledger below. */}
                <SelectTrigger className={`${statusTriggerCls(item.dispatch_status)} ${statusCtlSize(STATUS_CTL_W.Dispatch, isCompact)}`} data-testid={`dispatch-status-${item.id}`}>
                  <StatusGlyph status={item.dispatch_status || "Pending"} size={STATUS_ICON_PX} className="shrink-0" />
                  <SelectValue><span className="block min-w-0 flex-1 truncate" title={item.dispatch_status || "Pending"}>{item.dispatch_status || "Pending"}</span></SelectValue>
                </SelectTrigger>
                <SelectContent className="min-w-[140px]">
                  {(dispStatuses.length ? dispStatuses : DISPATCH_STATUSES.map(s => ({ key: s, label: s }))).map(s => (
                    <SelectItem key={s.key} value={s.key} className="text-xs py-1">
                      <span className="inline-flex items-center gap-1.5">
                        <StatusGlyph status={s.key} size={10} className="shrink-0" />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-[hsl(var(--muted))]">
                <StatusGlyph status={item.dispatch_status || "Pending"} size={11} />
                {dLabel("dispatch", item.dispatch_status || "Pending")}
              </span>
            )}
            {/* Ticket/Invoice ledger — drilling hides only the TR (Ticket) row;
                Invoice number + date stay for every division (Tushar 13-Jul). */}
            <TrInvoiceCell item={item} canEdit={canEdit} hoverReveal isCompact={isCompact} showTr={showTrFields} />
          </td>
        )}

        {/* Actions */}
        <td className={cellPadX2}>
          <div className="flex items-center justify-end gap-1.5">
            {/* Notes — shares the DETAILS / BACK / PROBLEM column vocabulary
                (icon + caption, w-7). Same hover-reveal as Details when empty;
                persistently visible (with counter) when populated. */}
            <div
              className={`inline-flex transition-opacity duration-150 ${
                (item.user_notes && item.user_notes.length > 0)
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto group-focus-within/row:opacity-100 group-focus-within/row:pointer-events-auto"
              }`}
            >
              <NotesCell item={item} canEdit={true} showCaption={!isCompact} />
            </div>
            {/* Details — always rendered (no layout shift), faded out until row hover/focus.
                Subtle slate gradient + matching tooltip. */}
            {onDetailsClick && (
              <div className="flex flex-col items-center opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto group-focus-within/row:opacity-100 group-focus-within/row:pointer-events-auto transition-opacity duration-150">
                <TooltipProvider delayDuration={0} skipDelayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onDetailsClick(item)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-well transition-colors text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40"
                        aria-label="Open item details"
                        data-testid={`details-btn-${item.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      sideOffset={6}
                      className="bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] shadow-sm text-[10px] font-medium tracking-wide px-2 py-1"
                    >
                      Details &amp; history
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {!isCompact && <span className="text-[8.5px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))]/70 leading-none mt-0.5" aria-hidden="true">Details</span>}
              </div>
            )}

            {canEdit && (
              <>
                {!isFirstStage && (
                  <div className="flex flex-col items-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onReturnClick(item)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-well transition-colors text-[hsl(var(--muted-foreground))]/70 hover:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                            aria-label="Send back to previous stage"
                            data-testid={`return-btn-${item.id}`}
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent><p>Send back to previous stage</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {!isCompact && <span className="text-[8.5px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))]/70 leading-none mt-0.5" aria-hidden="true">Back</span>}
                  </div>
                )}

                {stage === "Quality Control" && item.qc_status === "Failed" && !item.is_blocked && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onReturnToCustomer(item)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-well transition-colors text-[hsl(var(--muted-foreground))]/70 hover:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                          aria-label="Return failed part to customer"
                          data-testid={`return-customer-btn-${item.id}`}
                        >
                          <PackageX className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p>Return failed part to customer</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {item.is_blocked ? (
                  <div className="flex flex-col items-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onUnblock(item.id)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-ico-well bg-red-100 text-red-600 border border-red-200 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:border-red-900 dark:hover:bg-red-900/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                            aria-label="Unblock item"
                            data-testid={`unblock-btn-${item.id}`}
                          >
                            <ShieldOff className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top"><p>Unblock</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {!isCompact && <span className="text-[8.5px] uppercase tracking-wide font-semibold text-red-500/80 leading-none mt-0.5" aria-hidden="true">Unblock</span>}
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onBlockClick(item)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-well text-[hsl(var(--muted-foreground))]/70 hover:text-red-600 dark:hover:text-red-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                            aria-label="Report a problem"
                            data-testid={`problem-btn-${item.id}`}
                          >
                            <ShieldAlert className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top"><p>Problem</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {!isCompact && <span className="text-[8.5px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))]/70 leading-none mt-0.5" aria-hidden="true">Problem</span>}
                  </div>
                )}

                {/* Customer Design Approval gate (Phase 1b, drilling) — sits
                    beside Accept so cause (approve) and effect (unlock) read
                    together. Same pill family as COC/QD. */}
                {stage === "PO Received" && designGate && canEdit && (
                  <button
                    type="button"
                    onClick={() => setDesignApproved?.(item.id, !item.design_approved)}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-1.5 py-1 rounded rl-glass-hairline transition-colors ${
                      item.design_approved
                        ? "bg-green-500/10 text-green-600 rl-edge-green"
                        : "bg-amber-500/10 text-amber-600 rl-edge-amber hover:bg-amber-500/20"
                    }`}
                    title={item.design_approved
                      ? `Design approved${item.design_approved_by ? " · by " + item.design_approved_by : ""} — click to clear`
                      : "Customer design approval pending — click to approve"}
                    data-testid={`design-approval-${item.id}`}
                  >
                    {item.design_approved ? <CheckCircle2 className="w-3 h-3" /> : <Clock3 className="w-3 h-3" />}
                    {item.design_approved ? "Design OK" : "Design Appr."}
                  </button>
                )}
                {!isLastStage && (
                  <AdvanceButton
                    item={item}
                    stage={stage}
                    advanceable={advanceable}
                    isAnimating={isAnimating}
                    lockReason={lockReason}
                    stageColor={stageColor}
                    onAdvanceClick={onAdvanceClick}
                    isCompact={isCompact}
                  />
                )}
              </>
            )}
          </div>

          {/* Stage age badge */}
          {!isLastStage && (() => {
            const ageSecs = getStageAgeSecs(item);
            const col = ageColor(ageSecs);
            // Age earns its pixels: fresh items show a barely-there mono
            // time; only AGING items (5d+ amber, 10d+ red) graduate into an
            // hourglass pill — a column scan finds stuck rows by presence.
            const ageLabel = `${formatStageAge(ageSecs)} in this stage`;
            return (
              <div className={`flex items-center justify-end ${isCompact ? "mt-0.5" : "mt-1.5"} pr-0.5`} data-testid={`stage-age-${item.id}`} title={ageLabel} aria-label={ageLabel}>
                {col ? (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border rl-pill font-mono-data font-medium leading-none tabular-nums ${isCompact ? "px-1.5 py-0 text-[9px]" : "px-2 py-[2.5px] text-[10px]"}`}
                    style={{ color: col, borderColor: `${col}73`, background: `${col}12` }}
                  >
                    <Hourglass className={isCompact ? "w-[9px] h-[9px] shrink-0" : "w-[11px] h-[11px] shrink-0"} />
                    {formatStageAge(ageSecs)} <span className="font-sans font-normal not-italic opacity-80">in stage</span>
                  </span>
                ) : (
                  <span className={`font-mono-data leading-none tabular-nums text-[hsl(var(--muted-foreground))]/60 ${isCompact ? "text-[9px]" : "text-[10px]"}`}>
                    {formatStageAge(ageSecs)} <span className="font-sans">in stage</span>
                  </span>
                )}
              </div>
            );
          })()}
        </td>
      </tr>
    );
  },
  (prev, next) =>
    prev.item === next.item &&
    prev.item?.quality_dossier_ready === next.item?.quality_dossier_ready &&
    prev.item?.customer_acceptance === next.item?.customer_acceptance &&
    prev.item?.division === next.item?.division &&
    prev.item?.design_approved === next.item?.design_approved &&
    prev.item?.coc_status === next.item?.coc_status &&
    prev.item?.special_remark === next.item?.special_remark &&
    prev.canEdit === next.canEdit &&
    prev.showValue === next.showValue &&
    prev.showSpend === next.showSpend &&
    prev.clientColor === next.clientColor &&
    prev.clientLogoSvg === next.clientLogoSvg &&
    prev.isAnimating === next.isAnimating &&
    prev.isConfirmingDelete === next.isConfirmingDelete &&
    prev.advanceable === next.advanceable &&
    // density changes must re-render rows (affects Procurement dropdown sizing,
    // label abbreviations, and where the Spent figure lives). Without this
    // the new compact layout only appeared after a page refresh.
    prev.densityCfg?.isCompact === next.densityCfg?.isCompact &&
    prev.densityCfg?.descFont === next.densityCfg?.descFont &&
    prev.partDuplicateMap === next.partDuplicateMap &&
    prev.erpHighlight === next.erpHighlight &&
    prev.isAdmin === next.isAdmin &&
    prev.canErpEdit === next.canErpEdit &&
    prev.canDefineBom === next.canDefineBom &&
    prev.canBuyBom === next.canBuyBom
);
